import React, { useEffect, useState, useContext } from "react";
import { i18n } from "../../translate/i18n";
import { makeStyles } from "@material-ui/core/styles";
import { Button, CircularProgress, IconButton, Tooltip, Typography } from "@material-ui/core";
import LinkOffIcon from "@material-ui/icons/LinkOff";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import AddIcon from "@material-ui/icons/Add";
import { useHistory } from "react-router-dom";

import ChannelIcon from "../ChannelIcon";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { SocketContext } from "../../context/Socket/SocketContext";

/**
 * ContactChannelHub
 *
 * Phase 3.6 — Customer Channel Hub
 *
 * Displays all channels linked to a contact inside the ContactDrawer.
 * Extends the existing contact profile — does NOT create a new screen.
 *
 * Features:
 *   - Lists all active channel links (WA / FB / IG / Email / Telegram)
 *   - Shows unread count per channel
 *   - "פתח שיחה" navigates to the latest ticket on that channel
 *   - "נתק" soft-deletes the channel link
 *   - "חבר ערוץ" opens add-channel inline form
 *
 * Props:
 *   contactId: number
 *   companyId: number
 */

const CHANNEL_LABELS = {
  whatsapp:  "WhatsApp",
  facebook:  "Messenger",
  instagram: "Instagram",
  email:     "Email",
  telegram:  "Telegram",
};

const useStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === "dark";
  return {
    root: { display: "flex", flexDirection: "column", gap: 8 },
    channelRow: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "8px 10px",
      borderRadius: 10,
      background: isDark ? "rgba(255,255,255,0.04)" : "#f6faf7",
      border: isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid #e8f0ec",
    },
    channelInfo: { flex: 1, minWidth: 0 },
    channelName: {
      fontSize: 12,
      fontWeight: 700,
      color: isDark ? "#e6f7ec" : "#102b1f",
    },
    channelSub: {
      fontSize: 10,
      color: isDark ? "rgba(230,247,236,0.45)" : "#5f6f6a",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    unreadBadge: {
      background: "#ef4444",
      color: "#fff",
      borderRadius: 8,
      fontSize: 10,
      fontWeight: 700,
      padding: "1px 6px",
      minWidth: 18,
      textAlign: "center",
    },
    addBtn: {
      marginTop: 4,
      fontSize: 11,
      color: isDark ? "#7df0a8" : "#128c7e",
      textTransform: "none",
      padding: "4px 8px",
      borderRadius: 8,
      border: isDark ? "1px dashed rgba(37,211,102,0.3)" : "1px dashed rgba(18,140,126,0.3)",
    },
    addForm: {
      display: "flex",
      flexDirection: "column",
      gap: 8,
      padding: "10px",
      background: isDark ? "rgba(255,255,255,0.04)" : "#f6faf7",
      borderRadius: 10,
      border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #e8f0ec",
    },
    addSelect: {
      fontSize: 12,
      padding: "6px 8px",
      borderRadius: 6,
      border: isDark ? "1px solid rgba(255,255,255,0.15)" : "1px solid #ccc",
      background: isDark ? "#1a1f1c" : "#fff",
      color: isDark ? "#e6f7ec" : "#102b1f",
      width: "100%",
    },
    addInput: {
      fontSize: 12,
      padding: "6px 8px",
      borderRadius: 6,
      border: isDark ? "1px solid rgba(255,255,255,0.15)" : "1px solid #ccc",
      background: isDark ? "#1a1f1c" : "#fff",
      color: isDark ? "#e6f7ec" : "#102b1f",
      width: "100%",
    },
    addActions: { display: "flex", gap: 6, justifyContent: "flex-end" },
    saveBtn: {
      fontSize: 11,
      textTransform: "none",
      background: "#25d366",
      color: "#fff",
      padding: "4px 12px",
      borderRadius: 6,
      "&:hover": { background: "#128c7e" },
    },
    cancelBtn: {
      fontSize: 11,
      textTransform: "none",
      color: isDark ? "rgba(230,247,236,0.6)" : "#5f6f6a",
    },
    empty: {
      fontSize: 11,
      color: isDark ? "rgba(230,247,236,0.4)" : "#94a3b8",
      textAlign: "center",
      padding: "8px 0",
    },
  };
});

const ContactChannelHub = ({ contactId, companyId }) => {
  const classes = useStyles();
  const history = useHistory();
  const socketManager = useContext(SocketContext);

  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [newChannel, setNewChannel] = useState("whatsapp");
  const [newIdentifier, setNewIdentifier] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchLinks = async () => {
    if (!contactId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/contacts/${contactId}/channels`);
      setLinks(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLinks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId]);

  // Real-time: refresh when cross-channel unread arrives
  useEffect(() => {
    if (!companyId) return;
    const socket = socketManager.GetSocket(companyId);
    const handler = (data) => {
      if (
        data.action === "cross-channel-unread" &&
        data.contactId === contactId
      ) {
        fetchLinks();
      }
    };
    socket.on(`company-${companyId}-contact`, handler);
    return () => socket.off(`company-${companyId}-contact`, handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, contactId]);

  const handleOpenTicket = (ticketUuid) => {
    if (ticketUuid) history.push(`/tickets/${ticketUuid}`);
  };

  const handleUnlink = async (linkId) => {
    try {
      await api.delete(`/contacts/${contactId}/channels/${linkId}`);
      setLinks((prev) => prev.filter((l) => l.id !== linkId));
    } catch (err) {
      toastError(err);
    }
  };

  const handleAddChannel = async () => {
    if (!newIdentifier.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/contacts/${contactId}/channels`, {
        channel: newChannel,
        channelIdentifier: newIdentifier.trim(),
      });
      setLinks((prev) => [...prev, data]);
      setShowAdd(false);
      setNewChannel("whatsapp");
      setNewIdentifier("");
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 12 }}>
        <CircularProgress size={18} />
      </div>
    );
  }

  const activeLinks = links.filter((l) => l.isActive);

  return (
    <div className={classes.root}>
      {activeLinks.length === 0 && !showAdd && (
        <Typography className={classes.empty}>
          {i18n.t("contactDrawerSections.noLinkedChannels")}
        </Typography>
      )}

      {activeLinks.map((link) => (
        <div key={link.id} className={classes.channelRow}>
          <ChannelIcon channel={link.channel} size={22} />
          <div className={classes.channelInfo}>
            <div className={classes.channelName}>
              {CHANNEL_LABELS[link.channel] || link.channel}
            </div>
            <div className={classes.channelSub}>
              {link.channelIdentifier || link.displayName || "—"}
            </div>
          </div>
          {link.unreadCount > 0 && (
            <span className={classes.unreadBadge}>{link.unreadCount}</span>
          )}
          {link.latestTicketUuid && (
            <Tooltip title={i18n.t("contactDrawerSections.openChat")}>
              <IconButton size="small" onClick={() => handleOpenTicket(link.latestTicketUuid)}>
                <OpenInNewIcon style={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={i18n.t("contactDrawerSections.disconnectChannel")}>
            <IconButton size="small" onClick={() => handleUnlink(link.id)}>
              <LinkOffIcon style={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </div>
      ))}

      {showAdd ? (
        <div className={classes.addForm}>
          <select
            className={classes.addSelect}
            value={newChannel}
            onChange={(e) => setNewChannel(e.target.value)}
          >
            <option value="whatsapp">WhatsApp</option>
            <option value="facebook">Facebook Messenger</option>
            <option value="instagram">Instagram</option>
            <option value="email">Email</option>
            <option value="telegram">Telegram</option>
          </select>
          <input
            className={classes.addInput}
            placeholder={i18n.t("contactDrawerSections.channelIdPlaceholder")}
            value={newIdentifier}
            onChange={(e) => setNewIdentifier(e.target.value)}
          />
          <div className={classes.addActions}>
            <Button
              className={classes.cancelBtn}
              size="small"
              onClick={() => { setShowAdd(false); setNewIdentifier(""); }}
            >
              {i18n.t("contactDrawerSections.cancel")}
            </Button>
            <Button
              className={classes.saveBtn}
              size="small"
              onClick={handleAddChannel}
              disabled={saving || !newIdentifier.trim()}
            >
              {saving ? <CircularProgress size={12} /> : i18n.t("contactDrawerSections.connect")}
            </Button>
          </div>
        </div>
      ) : (
        <Button
          className={classes.addBtn}
          size="small"
          startIcon={<AddIcon style={{ fontSize: 14 }} />}
          onClick={() => setShowAdd(true)}
        >
          {i18n.t("contactDrawerSections.connectNewChannel")}
        </Button>
      )}
    </div>
  );
};

export default ContactChannelHub;
